import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";

// Copilot client state (COP-UX-141): the launcher panel and the full workspace share
// one governed session; opening/closing/navigating never discards conversation state
// (the server-side session is authoritative; only lightweight UI state lives here).
const CopilotContext = createContext(null);
const SESSION_KEY = "zelligio-copilot-session";
const OWNER_KEY = "zelligio-copilot-owner";

export function CopilotProvider({ children }) {
  const auth = useAuth();
  const userId = auth?.user?.id || null;
  const [panelOpen, setPanelOpen] = useState(false);
  const [sessionId, setSessionIdState] = useState(() => sessionStorage.getItem(SESSION_KEY) || null);
  const [panelContext, setPanelContext] = useState(null);
  const panelContextRef = useRef(null);

  const setSessionId = useCallback((id) => {
    setSessionIdState(id);
    if (id) sessionStorage.setItem(SESSION_KEY, id);
    else sessionStorage.removeItem(SESSION_KEY);
  }, []);

  // V5-H2 (HIS-009): a device reused by another account never inherits the previous account's conversation pointer
  useEffect(() => {
    const owner = sessionStorage.getItem(OWNER_KEY);
    if (userId && owner && owner !== userId) {
      setSessionIdState(null);
      sessionStorage.removeItem(SESSION_KEY);
      panelContextRef.current = null;
      setPanelContext(null);
      setPanelOpen(false);
    }
    if (userId) sessionStorage.setItem(OWNER_KEY, userId);
    if (!userId) { setSessionIdState(null); sessionStorage.removeItem(SESSION_KEY); }
  }, [userId]);

  const openPanel = useCallback(() => {
    panelContextRef.current = null;
    setPanelContext(null);
    setPanelOpen(true);
  }, []);
  const closePanel = useCallback(() => setPanelOpen(false), []);
  // Contextual launch (COP-UX-137): a different governed object starts a fresh
  // conversation bound to that object (one active context per conversation,
  // COP-MEM-022); re-opening the same object keeps the existing conversation.
  const openWithContext = useCallback((context) => {
    const prev = panelContextRef.current;
    panelContextRef.current = context || null;
    if (context && (!prev || prev.id !== context.id)) setSessionId(null);
    setPanelContext(context || null);
    setPanelOpen(true);
  }, [setSessionId]);

  return (
    <CopilotContext.Provider value={{
      panelOpen, openPanel, closePanel, openWithContext,
      sessionId, setSessionId,
      panelContext, setPanelContext,
    }}>
      {children}
    </CopilotContext.Provider>
  );
}

export function useCopilot() {
  return useContext(CopilotContext);
}
